import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate } from 'react-router-dom';
import { X as XIcon, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/auth.store';
import { useBrandingStore } from '@/store/branding.store';
import { LOGO_REVERSE } from '@/hooks/useThemedLogo';
import { getApiError, api } from '@/services/api';
import type { User } from '@/types';
import { toast } from '@/components/common/Toast';

const schema = z.object({
  email:    z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  terms:    z.literal<boolean>(true, {
    errorMap: () => ({ message: 'You must accept the Terms of Service to continue' }),
  }),
});
type FormValues = z.infer<typeof schema>;

// ── Inline SVG social icons (no extra dependency) ─────────────────────────────
function IconX() {
  return (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.734-8.85L1.254 2.25H8.08l4.259 5.629L18.244 2.25zM17.083 19.77h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
function IconLinkedIn() {
  return (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}
function IconFacebook() {
  return (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

/**
 * Fallback wording for a refusal that arrives without a message of its own.
 * The server sends one for the cases it can explain; these cover the rest.
 */
const SSO_ERRORS: Record<string, string> = {
  auth_failed:      'The identity provider did not confirm your sign-in.',
  provider_refused: 'The identity provider did not confirm your sign-in.',
  not_configured:   'Single sign-on is not finished being set up. Ask an administrator.',
  client_user:      'This is a customer portal account. Sign in with your portal password instead of single sign-on.',
  domain:           'That address is not permitted to sign in with single sign-on.',
  no_domain_policy: 'Single sign-on is not finished being set up. Ask an administrator to set the allowed domains.',
  no_provisioning:  'No account exists for that address.',
  provision_failed: 'Single sign-on did not complete. Please try again.',
};

// ── Legal content ──────────────────────────────────────────────────────────────

const TERMS_SECTIONS = [
  {
    heading: '1. Acceptance of Terms',
    body: `By accessing or using the GRC Platform ("Platform"), you confirm that you are authorised by your organisation to do so and that you agree to be bound by these Terms of Service ("Terms"). If you do not agree, you must not use the Platform. These Terms apply to all users including administrators, auditors, risk managers, and read-only reviewers.`,
  },
  {
    heading: '2. Permitted Use',
    body: `The Platform is provided exclusively for internal governance, risk, and compliance activities within your licensed organisation. You may use the Platform to manage risks, controls, audits, findings, policies, assessments, and related documentation. You may not use the Platform for any unlawful purpose, to store data that violates applicable law, or to attempt to gain unauthorised access to other users' data or system components.`,
  },
  {
    heading: '3. User Accounts & Security',
    body: `Each user account is personal and non-transferable. You are responsible for maintaining the confidentiality of your credentials, including your password and any multi-factor authentication codes. You must notify your system administrator immediately upon discovery of any unauthorised use of your account. The Platform supports multi-factor authentication (MFA); administrators may enforce MFA as a requirement for access.`,
  },
  {
    heading: '4. Data Ownership',
    body: `All data, documents, and records you upload or create within the Platform remain the property of your organisation. The Platform does not claim ownership over your content. You grant the Platform the limited right to process, store, and display your content solely for the purpose of providing the service to you.`,
  },
  {
    heading: '5. Evidence & Document Uploads',
    body: `You may upload files as evidence against controls and requirements. Permitted file types include images (JPEG, PNG, GIF, WebP), PDF, Microsoft Word, Excel, plain text, and CSV files up to 20 MB each. You are solely responsible for ensuring that uploaded files do not contain malware, unlicensed material, or confidential information that should not be stored in this system.`,
  },
  {
    heading: '6. Availability & Maintenance',
    body: `The Platform is provided on a best-efforts basis. Scheduled maintenance windows may cause temporary unavailability. The Platform operator does not guarantee uninterrupted access and shall not be liable for losses resulting from downtime, provided reasonable notice of maintenance is given where practicable.`,
  },
  {
    heading: '7. Prohibited Conduct',
    body: `You must not attempt to reverse-engineer, decompile, or extract the source code of the Platform. You must not introduce automated scripts, bots, or crawlers that place excessive load on the system. You must not attempt to exploit security vulnerabilities. Violations may result in immediate account suspension and may be referred to appropriate authorities.`,
  },
  {
    heading: '8. Intellectual Property',
    body: `The Platform software, design, trademarks, and documentation are the intellectual property of the Platform operator. Nothing in these Terms transfers any intellectual property rights to you. Your organisation retains full ownership of all data and reports generated from your own content.`,
  },
  {
    heading: '9. Limitation of Liability',
    body: `To the maximum extent permitted by applicable law, the Platform operator shall not be liable for indirect, incidental, special, consequential, or punitive damages, including loss of data or business interruption, even if advised of such possibility. The operator's total liability shall not exceed the fees paid by your organisation in the twelve months preceding the claim.`,
  },
  {
    heading: '10. Changes to These Terms',
    body: `These Terms may be updated from time to time. Continued use of the Platform following notification of changes constitutes acceptance of the revised Terms. Material changes will be communicated via in-application notice or email to registered administrators.`,
  },
  {
    heading: '11. Governing Law',
    body: `These Terms are governed by the laws of the jurisdiction in which the Platform operator is registered, without regard to conflict-of-law provisions. Any disputes shall be resolved in the courts of that jurisdiction.`,
  },
];

const PRIVACY_SECTIONS = [
  {
    heading: '1. Who We Are',
    body: `This Privacy Policy explains how the GRC Platform ("we", "us", "Platform") collects, uses, stores, and protects personal information submitted by users within your organisation's licensed instance. We act as a data processor on behalf of your organisation (the data controller).`,
  },
  {
    heading: '2. What Personal Data We Collect',
    body: `We collect and process the following personal data strictly to operate the Platform:\n• Identity data: first name, last name, email address, and job role.\n• Authentication data: hashed passwords, MFA secret keys (never stored in plain text), session tokens.\n• Activity data: audit log entries including login events, record creation, updates, and deletions, along with timestamps and IP addresses.\n• Uploaded content: evidence files, policy documents, and notes that may incidentally contain personal information.`,
  },
  {
    heading: '3. How We Use Personal Data',
    body: `Personal data is used solely to:\n• Authenticate and authorise users.\n• Maintain audit trails for compliance and governance purposes.\n• Send system-generated notifications (e.g., task assignments, findings, assessments).\n• Provide administrative oversight features to system administrators.\n\nWe do not use personal data for advertising, profiling, or any purpose unrelated to the operation of the Platform.`,
  },
  {
    heading: '4. Legal Basis for Processing',
    body: `Where applicable data protection law requires a legal basis for processing, we rely on: (a) the performance of a contract — providing the Platform service to your organisation; (b) legitimate interests — maintaining security, preventing fraud, and operating audit logs; and (c) compliance with legal obligations where retention of records is required by law.`,
  },
  {
    heading: '5. Data Retention',
    body: `User account data is retained for as long as an account is active. Deactivated accounts are retained for a configurable period (default: 90 days) to preserve audit integrity before permanent deletion. Audit logs are retained according to the retention period configured by your administrator (default: 90 days). Uploaded evidence documents are retained until deleted by an authorised user or until the assessment is permanently removed.`,
  },
  {
    heading: '6. Data Security',
    body: `We implement appropriate technical and organisational measures to protect personal data, including:\n• Passwords hashed using bcrypt with a minimum cost factor of 12.\n• Transport encryption via HTTPS/TLS for all data in transit.\n• Access controls enforced by role-based permissions.\n• Multi-factor authentication support.\n• Audit logging of all access and change events.\n\nDespite these measures, no system is completely immune to security risks, and you should report suspected breaches to your administrator immediately.`,
  },
  {
    heading: '7. Sharing of Personal Data',
    body: `We do not sell, rent, or share personal data with third parties for commercial purposes. Data may be disclosed to:\n• Sub-processors providing infrastructure (e.g., database hosting), who are bound by equivalent data protection obligations.\n• Law enforcement or regulatory authorities when required by applicable law.\n• Your organisation's authorised administrators who have full access to all user records within their instance.`,
  },
  {
    heading: '8. Your Rights',
    body: `Depending on your jurisdiction, you may have the right to access, correct, delete, or restrict processing of your personal data. You may also have the right to data portability. Requests should be directed to your organisation's system administrator, who acts as the point of contact for data subject rights in your instance. We will provide reasonable assistance to your organisation in fulfilling such requests.`,
  },
  {
    heading: '9. Cookies & Local Storage',
    body: `The Platform uses browser local storage to maintain authentication session tokens. No third-party advertising or tracking cookies are used. Session tokens are invalidated on logout and expire automatically after a configurable idle period.`,
  },
  {
    heading: '10. Changes to This Policy',
    body: `This Privacy Policy may be updated periodically to reflect changes in law or Platform functionality. Material changes will be communicated via in-application notice. Continued use of the Platform following notice constitutes acceptance of the revised Policy.`,
  },
  {
    heading: '11. Contact',
    body: `For privacy-related queries, please contact your organisation's designated system administrator or data protection officer. For Platform-level concerns, contact the Platform operator via the support channel provided to your organisation.`,
  },
];

type LegalType = 'terms' | 'privacy';

function LegalModal({
  type,
  appName,
  onClose,
}: {
  type: LegalType;
  appName: string;
  onClose: () => void;
}) {
  const isTerms   = type === 'terms';
  const title     = isTerms ? 'Terms of Service' : 'Privacy Policy';
  const sections  = isTerms ? TERMS_SECTIONS : PRIVACY_SECTIONS;
  const effective = 'Effective date: 1 January 2026';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex w-full max-w-2xl flex-col rounded-2xl bg-surface shadow-2xl"
           style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 rounded-t-2xl border-t-[3px] border-dl-orange bg-navy px-6 py-5 text-white">
          <div>
            <p className="text-xs font-medium text-white/60 mb-0.5">{appName}</p>
            <h2 className="text-xl font-bold">{title}</h2>
            <p className="mt-0.5 text-xs text-white/60">{effective}</p>
          </div>
          <button
            onClick={onClose}
            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 text-sm text-gray-700 leading-relaxed">
          <p className="text-xs text-gray-400 italic">
            Please read these {title.toLowerCase()} carefully before using the Platform.
          </p>
          {sections.map((s) => (
            <div key={s.heading}>
              <h3 className="mb-1.5 text-sm font-bold text-gray-900">{s.heading}</h3>
              <p className="whitespace-pre-line text-gray-600">{s.body}</p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 rounded-b-2xl border-t border-gray-100 bg-gray-50 px-6 py-4">
          <button
            onClick={onClose}
            className="btn-primary px-5 py-2 font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { login, verifyMfa, mfaRequired, loginPending, loginError, isAuthenticated } = useAuth();
  const token = useAuthStore((s) => s.token);
  const {
    appName,
    twitterUrl, linkedinUrl, facebookUrl, copyrightText,
  } = useBrandingStore();
  const [legalModal, setLegalModal] = useState<LegalType | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [ssoConfig, setSsoConfig] = useState<{ protocol: string; breakglassEnabled: boolean } | null>(null);
  const [ssoExchanging, setSsoExchanging] = useState(false);
  /** What the server said about the address currently typed. */
  const [ssoMatch, setSsoMatch] = useState<{ sso: boolean; protocol?: string } | null>(null);
  const [ssoChecking, setSsoChecking] = useState(false);
  /** Set when someone deliberately crosses to the other door. */
  const [preferPassword, setPreferPassword] = useState(false);

  // Fetch SSO config on mount
  useEffect(() => {
    api.get<{ data: { protocol: string; breakglassEnabled?: boolean } }>('/auth/sso/config')
      .then(r => {
        const cfg = r.data.data;
        setSsoConfig({ protocol: cfg.protocol, breakglassEnabled: cfg.breakglassEnabled ?? true });
      })
      .catch(() => { /* SSO not configured — show normal login */ });
  }, []);

  /**
   * What comes back from an SSO callback.
   *
   * The callback hands over a one-time code, never the token itself: a token
   * in the query string is written to browser history and leaks through the
   * Referer header of the first outbound link. The code is exchanged here
   * over a same-origin POST and is dead the moment it is used.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoCode    = params.get('sso_code');
    const ssoError   = params.get('sso_error');
    const ssoMessage = params.get('sso_message');
    const reason     = params.get('reason');

    // Clear the address bar before anything awaits, so the code is not left
    // sitting in the URL while the round trip happens.
    if (ssoCode || ssoError || reason) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (ssoCode) {
      setSsoExchanging(true);
      api.post<{ data: { accessToken: string; user: User } }>('/auth/sso/exchange', { code: ssoCode })
        .then(r => {
          const { accessToken, user } = r.data.data;
          useAuthStore.getState().setAuth(accessToken, user);
        })
        .catch(err => toast.error(getApiError(err)))
        .finally(() => setSsoExchanging(false));
    }

    // The refusals are written to be acted on — "you are a portal user, use
    // your password" — and were previously stripped from the URL and thrown
    // away, leaving the person staring at an unchanged login page.
    if (ssoError) {
      toast.error(ssoMessage || SSO_ERRORS[ssoError] || 'Single sign-on did not complete. Please try again.');
    }

    if (reason === 'timeout') {
      toast.info('You were signed out due to inactivity. Please sign in again.');
    }
  }, []);

  const breakglassOn = ssoConfig?.breakglassEnabled ?? true;

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', terms: false as unknown as true },
  });

  /**
   * Which door belongs to this address.
   *
   * Everyone sees the ordinary form; as soon as the address is recognised as
   * belonging to an SSO domain, the password field gives way to the provider.
   * The answer comes from the configured domain list, never from whether an
   * account exists — that would make this an enumeration oracle.
   */
  const emailValue = watch('email');
  useEffect(() => {
    const email = (emailValue ?? '').trim().toLowerCase();
    const domain = email.includes('@') ? email.split('@')[1] : '';
    if (!domain || !domain.includes('.')) { setSsoMatch(null); return; }

    let cancelled = false;
    setSsoChecking(true);
    // Wait for typing to settle: one request per address, not per keystroke.
    const timer = setTimeout(() => {
      api.post<{ data: { sso: boolean; protocol?: string } }>('/auth/sso/lookup', { email })
        .then(r => { if (!cancelled) setSsoMatch(r.data.data); })
        .catch(() => { if (!cancelled) setSsoMatch(null); })
        .finally(() => { if (!cancelled) setSsoChecking(false); });
    }, 450);

    return () => { cancelled = true; clearTimeout(timer); window.clearTimeout(timer); setSsoChecking(false); };
  }, [emailValue]);

  // A different address is a different question; forget the last choice.
  useEffect(() => { setPreferPassword(false); }, [(emailValue ?? '').split('@')[1]]);

  const useSso = Boolean(ssoMatch?.sso) && !preferPassword;

  const startSso = () => {
    const protocol = (ssoMatch?.protocol ?? ssoConfig?.protocol ?? '').toLowerCase();
    if (protocol !== 'saml' && protocol !== 'oidc') {
      toast.error('Single sign-on is not configured. Ask an administrator.');
      return;
    }
    window.location.href = `/api/v1/auth/sso/${protocol}/login`;
  };

  if (token || isAuthenticated) return <Navigate to="/dashboard" replace />;

  const onSubmit = (values: FormValues) => login(values);
  const onMfaSubmit = (e: React.FormEvent) => { e.preventDefault(); verifyMfa(mfaCode); };

  const hasSocial = twitterUrl || linkedinUrl || facebookUrl;
  const year      = new Date().getFullYear();
  const copyright = copyrightText || `© ${year} ${appName}. All rights reserved.`;

  return (
    <div className="flex min-h-screen flex-col bg-surface-2">

      {/* Thin brand accent line at very top */}
      <div className="h-1 w-full flex-none bg-dl-orange" />

      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">

          {/* ── Brand header card: navy with the orange top edge and the
               reverse logo (design system › SignInCard) ─────────────────── */}
          <div className="flex flex-col items-center gap-4 rounded-t-2xl border-t-[3px] border-dl-orange bg-navy px-8 py-10">
            <img src={LOGO_REVERSE} alt="Dual Layer IT" className="h-14 w-auto max-w-[240px] object-contain" />
            <div className="text-center">
              <h1 className="text-3xl text-ink-d">{appName}</h1>
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.12em] text-ink-soft">Governance · Risk · Compliance</p>
            </div>
          </div>

          {/* ── Sign-in form ──────────────────────────────────────────────── */}
          <div className="rounded-b-2xl bg-surface px-8 pb-8 pt-7 shadow-2xl">
            {ssoExchanging ? (
              /* Back from the identity provider, trading the code for a session. */
              <div className="flex flex-col items-center gap-3 py-10">
                <Loader2 className="text-accent h-6 w-6 animate-spin" />
                <p className="text-sm text-gray-500">Completing sign-in…</p>
              </div>
            ) : mfaRequired ? (
              <>
                <h2 className="mb-2 text-lg font-semibold text-gray-800">Two-Factor Authentication</h2>
                <p className="mb-5 text-sm text-gray-500">Enter the 6-digit code from your authenticator app.</p>

                <form onSubmit={onMfaSubmit} className="space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Verification code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      className="input text-center text-lg tracking-[0.3em]"
                      placeholder="000000"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      autoFocus
                    />
                  </div>

                  {loginError && (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
                      {getApiError(loginError)}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loginPending || mfaCode.length !== 6}
                    className="btn-primary w-full justify-center py-2.5 font-semibold disabled:opacity-60"
                  >
                    {loginPending ? 'Verifying…' : 'Verify & Sign in'}
                  </button>
                </form>
              </>
            ) : (
              <>
                <h2 className="mb-5 text-lg font-semibold text-gray-800">Sign in to your account</h2>

                <form onSubmit={useSso ? (e) => { e.preventDefault(); startSso(); } : handleSubmit(onSubmit)} className="space-y-4">
                  {/* Email */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Email address</label>
                    <input
                      {...register('email')}
                      type="email"
                      className="input"
                      placeholder="you@company.com"
                      autoComplete="email"
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                    )}
                    {ssoChecking && (
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-400">
                        <Loader2 className="h-3 w-3 animate-spin" /> Checking how your organisation signs in…
                      </p>
                    )}
                  </div>

                  {useSso ? (
                    /* The address belongs to a domain that signs in through the
                       identity provider, so the password field would only be a
                       dead end. Nobody has to know which door is theirs. */
                    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                      <p className="flex items-start gap-2 text-sm text-gray-600">
                        <ShieldCheck className="text-accent mt-0.5 h-4 w-4 shrink-0" />
                        <span>Your organisation signs in with single sign-on.</span>
                      </p>
                    </div>
                  ) : (
                    /* Password */
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Password</label>
                      <input
                        {...register('password')}
                        type="password"
                        className="input"
                        placeholder="••••••••"
                        autoComplete="current-password"
                      />
                      {errors.password && (
                        <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
                      )}
                    </div>
                  )}

                  {/* Terms & Conditions */}
                  <div className="space-y-1 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-start gap-2.5">
                      <input
                        {...register('terms')}
                        id="terms"
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300"
                        style={{ accentColor: 'rgb(var(--accent-600))' }}
                      />
                      <label htmlFor="terms" className="cursor-pointer text-sm text-gray-600 leading-snug">
                        I have read and agree to the{' '}
                        <button
                          type="button"
                          onClick={() => setLegalModal('terms')}
                          className="text-accent font-medium underline underline-offset-2 hover:opacity-80"
                        >
                          Terms of Service
                        </button>{' '}
                        and{' '}
                        <button
                          type="button"
                          onClick={() => setLegalModal('privacy')}
                          className="text-accent font-medium underline underline-offset-2 hover:opacity-80"
                        >
                          Privacy Policy
                        </button>
                      </label>
                    </div>
                    {errors.terms && (
                      <p className="pl-6 text-xs text-red-600">{errors.terms.message}</p>
                    )}
                  </div>

                  {/* API error */}
                  {loginError && (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
                      {getApiError(loginError)}
                    </p>
                  )}

                  {useSso ? (
                    <button
                      type="button"
                      onClick={startSso}
                      className="btn-primary w-full justify-center py-2.5 font-semibold"
                    >
                      <KeyRound className="h-4 w-4" />
                      Continue with single sign-on
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loginPending}
                      className="btn-primary w-full justify-center py-2.5 font-semibold disabled:opacity-60"
                    >
                      {loginPending ? 'Signing in…' : 'Sign in'}
                    </button>
                  )}

                  {/* The way across, when the automatic choice is wrong — a
                      contractor on a staff domain, or an administrator who
                      needs the password door while the IdP is down. Hidden
                      when breakglass is off, because the server refuses a
                      staff password then and offering it would be a lie. */}
                  {ssoMatch?.sso && breakglassOn && (
                    <button
                      type="button"
                      onClick={() => setPreferPassword(p => !p)}
                      className="w-full text-center text-xs text-gray-500 underline underline-offset-2 hover:text-gray-700"
                    >
                      {useSso ? 'Use my password instead' : 'Use single sign-on instead'}
                    </button>
                  )}
                </form>
              </>
            )}

            {/* Social links */}
            {hasSocial && (
              <div className="mt-6 flex justify-center gap-4 border-t border-gray-100 pt-5">
                {twitterUrl && (
                  <a href={twitterUrl} target="_blank" rel="noopener noreferrer" title="X / Twitter"
                    className="text-gray-400 transition-colors hover:text-gray-700">
                    <IconX />
                  </a>
                )}
                {linkedinUrl && (
                  <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" title="LinkedIn"
                    className="text-gray-400 transition-colors hover:text-gray-700">
                    <IconLinkedIn />
                  </a>
                )}
                {facebookUrl && (
                  <a href={facebookUrl} target="_blank" rel="noopener noreferrer" title="Facebook"
                    className="text-gray-400 transition-colors hover:text-gray-700">
                    <IconFacebook />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Copyright */}
          <p className="mt-4 text-center text-xs text-gray-400">{copyright}</p>
        </div>
      </div>

      {/* Legal modals */}
      {legalModal && (
        <LegalModal
          type={legalModal}
          appName={appName}
          onClose={() => setLegalModal(null)}
        />
      )}
    </div>
  );
}
